import { useState } from "react";
import { useMutation } from "convex/react";
import { useConvexAuth } from "convex/react";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircleIcon, SparkleIcon, CheckIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/primitives";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { AIProviderStep, type AIProviderChoice } from "./steps/AIProviderStep";
import { useTranslation } from "@/packages/utils";
import { useAccentColor } from "@/hooks/use-accent-color";
import {
  OrgContextForm,
  isCompleteOrgContext,
} from "@/components/workspace/OrgContextForm";
import type { OrgContextAnswers } from "@/components/audits/NewAuditWizard/types";

const ACCENT_COLOR_PRESET_KEYS = [
  { hex: "#5a8a72", key: "sage" },
  { hex: "#5b7ea8", key: "steel" },
  { hex: "#8b7aad", key: "lavender" },
  { hex: "#b86e6e", key: "rose" },
  { hex: "#c08a5a", key: "amber" },
  { hex: "#5a9ea8", key: "teal" },
  { hex: "#6e6da0", key: "indigo" },
] as const;

// ── Option data ──

const INDUSTRY_KEYS = [
  "technologySaas",
  "financialServices",
  "healthcare",
  "manufacturing",
  "retailEcommerce",
  "energyUtilities",
  "government",
  "education",
  "other",
] as const;

const INDUSTRY_DB_VALUES: Record<string, string> = {
  technologySaas: "Technology / SaaS",
  financialServices: "Financial Services",
  healthcare: "Healthcare",
  manufacturing: "Manufacturing",
  retailEcommerce: "Retail / E-commerce",
  energyUtilities: "Energy / Utilities",
  government: "Government",
  education: "Education",
  other: "Other",
};

const PROFILING_SIZES = ["1-20", "21-50", "51-200", "201-500"] as const;
type ProfilingCompanySize = (typeof PROFILING_SIZES)[number];

const JURISDICTION_KEYS = [
  "unitedStates",
  "europeanUnion",
  "unitedKingdom",
  "canada",
  "australia",
  "other",
] as const;

const JURISDICTION_DB_VALUES: Record<string, string> = {
  unitedStates: "United States",
  europeanUnion: "European Union",
  unitedKingdom: "United Kingdom",
  canada: "Canada",
  australia: "Australia",
  other: "Other",
};

// ── Animation variants ──

const ease = [0.25, 0.46, 0.45, 0.94] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 20 : -20,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.35, ease },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -20 : 20,
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease,
    },
  }),
};

// ── Selection chip component ──

function SelectionChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      className={`
        px-4 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-200
        border cursor-pointer
        ${
          selected
            ? "border-accent bg-accent/8 text-accent"
            : "border-border bg-white text-primary hover:border-border hover:bg-card-hover"
        }
      `}
    >
      {label}
    </motion.button>
  );
}

// ── Panel content per step ──

const PANEL_CONTENT = [
  {
    headline: "common:onboarding.aiSetup.setupHeadline",
    subline: "common:onboarding.aiSetup.setupSubline",
  },
  {
    headline: "common:onboarding.moreInfo.headline",
    subline: "common:onboarding.moreInfo.subline",
  },
  {
    headline: "common:onboarding.step3.panelHeadline",
    subline: "common:onboarding.step3.panelSubline",
  },
  {
    headline: "common:onboarding.aiSetup.headline",
    subline: "common:onboarding.aiSetup.subline",
  },
  {
    headline: "common:onboarding.step4.title",
    subline: "common:onboarding.step4.subtitle",
  },
] as const;

const TOTAL_STEPS = 5;

// ── Main component ──

export function OnboardingWizard() {
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();
  const { t } = useTranslation(["common"]);
  const createWorkspace = useMutation(api.workspaces.create);
  const saveAiProvider = useMutation(api.workspace_ai_provider.save);

  // Form state
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState<ProfilingCompanySize | "">("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [aiProviderChoice, setAiProviderChoice] =
    useState<AIProviderChoice | null>(null);
  const [aiProviderVerified, setAiProviderVerified] = useState(true);
  const [withDemoData, setWithDemoData] = useState(false);
  const [orgContext, setOrgContext] = useState<Partial<OrgContextAnswers>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step state
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  useAccentColor(accentColor);

  const goToStep = (next: number) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const canProceedFromDetails =
    name.trim().length > 0 &&
    industry.length > 0 &&
    companySize.length > 0 &&
    jurisdiction.length > 0;

  const canProceedFromStep0 = name.trim().length > 0;
  const canProceedFromOrgContext = isCompleteOrgContext(orgContext);

  // Determine if the current step allows proceeding
  function getCanProceed(): boolean {
    switch (step) {
      case 0:
        return canProceedFromStep0;
      case 1:
        return canProceedFromDetails;
      case 2:
        return canProceedFromOrgContext;
      case 3:
        return aiProviderVerified;
      case 4:
        return true; // start mode is always valid
      default:
        return false;
    }
  }

  function handleNext() {
    if (step === 3) {
      goToStep(4);
    } else if (step === 4) {
      handleSubmit();
    } else {
      goToStep(step + 1);
    }
  }

  function handleBack() {
    if (step > 0) {
      goToStep(step - 1);
    }
  }

  async function handleSubmit() {
    if (!canProceedFromDetails) return;

    setIsSubmitting(true);
    try {
      const { slug, workspaceId, demoWorkspaceSlug } = await createWorkspace({
        name: name.trim(),
        companyName: companyName.trim() || undefined,
        industry: INDUSTRY_DB_VALUES[industry] ?? industry,
        companySize: companySize ? `${companySize} employees` : "",
        profilingCompanySize: companySize || undefined,
        jurisdiction: JURISDICTION_DB_VALUES[jurisdiction] ?? jurisdiction,
        withDemoData,
        accentColor: accentColor || undefined,
        orgContext: isCompleteOrgContext(orgContext) ? orgContext : undefined,
      });

      // Save AI provider config after workspace creation
      if (
        aiProviderChoice &&
        aiProviderChoice.provider === "byom_openai_compatible" &&
        aiProviderChoice.byomConfig
      ) {
        try {
          await saveAiProvider({
            workspaceId,
            provider: "byom_openai_compatible",
            byomRuntime: aiProviderChoice.byomConfig.runtime,
            byomEndpointUrl: aiProviderChoice.byomConfig.endpointUrl,
            byomModelName: aiProviderChoice.byomConfig.modelName,
            byomVisionModelName:
              aiProviderChoice.byomConfig.visionModelName || undefined,
            byomApiKey: aiProviderChoice.byomConfig.apiKey || undefined,
          });
        } catch {
          // Non-fatal — workspace was created, AI config can be set later in settings
          toast.error(t("common:onboarding.errors.aiConfigError"));
        }
      }
      // builtin_openai is the default — no save needed

      if (withDemoData && demoWorkspaceSlug) {
        toast.info(t("common:onboarding.errors.demoWorkspaceInfo"));
      }
      navigate({
        to: "/$workspaceSlug/dashboard",
        params: { workspaceSlug: slug },
      });
    } catch (_err) {
      toast.error(t("common:onboarding.errors.creationFailed"));
      setIsSubmitting(false);
    }
  }

  const handleExitOnboarding = () => {
    navigate({ to: "/auth" });
  };

  const panelContent = PANEL_CONTENT[step - 1] ?? PANEL_CONTENT[0];
  const headline = t(panelContent.headline);
  const subline = t(panelContent.subline);

  const isLastStep = step === TOTAL_STEPS - 1;
  const isFirstStep = step === 0;
  const canProceed = getCanProceed();

  return (
    <AuthLayout
      headline={headline}
      subline={subline}
      wide
      onBrandClick={isAuthenticated ? undefined : handleExitOnboarding}
    >
      {/* Progress bar */}
      <div className="mb-4 flex gap-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-1 flex-1 rounded-full overflow-hidden bg-border/50"
          >
            <motion.div
              className="h-full bg-accent rounded-full"
              initial={false}
              animate={{ width: step >= i ? "100%" : "0%" }}
              transition={{ duration: 0.4, ease }}
            />
          </div>
        ))}
      </div>

      {/* Navigation buttons — directly below progress bar on ALL steps */}
      <div className="mb-6 flex items-center gap-3">
        {!isFirstStep && (
          <button
            type="button"
            onClick={handleBack}
            className="rounded-full border-2 border-accent px-5 py-2 text-[13px] font-semibold text-accent transition-colors hover:bg-accent/8 cursor-pointer"
          >
            {t("onboarding.actions.back")}
          </button>
        )}
        <button
          type="button"
          disabled={!canProceed || isSubmitting}
          onClick={handleNext}
          className="rounded-full bg-accent px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {isLastStep
            ? isSubmitting
              ? t("onboarding.actions.creatingWorkspace")
              : t("onboarding.actions.createWorkspace")
            : t("onboarding.actions.continue")}
        </button>
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        {step === 0 ? (
          <motion.div
            key="step-0"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepName
              name={name}
              onNameChange={setName}
              companyName={companyName}
              onCompanyNameChange={setCompanyName}
              accentColor={accentColor}
              onAccentColorChange={setAccentColor}
            />
          </motion.div>
        ) : step === 1 ? (
          <motion.div
            key="step-1"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepDetails
              industry={industry}
              companySize={companySize}
              jurisdiction={jurisdiction}
              onIndustryChange={setIndustry}
              onCompanySizeChange={setCompanySize}
              onJurisdictionChange={setJurisdiction}
            />
          </motion.div>
        ) : step === 2 ? (
          <motion.div
            key="step-2"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepOrgContextOnboarding
              answers={orgContext}
              onAnswersChange={setOrgContext}
            />
          </motion.div>
        ) : step === 3 ? (
          <motion.div
            key="step-3"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <AIProviderStep
              onChoiceChange={(choice, verified) => {
                setAiProviderChoice(choice);
                setAiProviderVerified(verified);
              }}
            />
          </motion.div>
        ) : (
          <motion.div
            key="step-4"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <StepStartMode
              withDemoData={withDemoData}
              onWithDemoDataChange={setWithDemoData}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}

// ── Step 1: Workspace name + Company name ──

function StepName({
  name,
  onNameChange,
  companyName,
  onCompanyNameChange,
  accentColor,
  onAccentColorChange,
}: {
  name: string;
  onNameChange: (v: string) => void;
  companyName: string;
  onCompanyNameChange: (v: string) => void;
  accentColor: string;
  onAccentColorChange: (v: string) => void;
}) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex flex-col">
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("onboarding.step1.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("onboarding.step1.subtitle")}
        </p>
      </motion.div>

      <div className="mt-6 flex flex-col gap-4">
        {/* Company Name — above workspace name */}
        <motion.div
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="company-name"
            className="text-[13px] font-medium text-secondary"
          >
            {t("onboarding.step1.companyName")}
          </label>
          <Input
            id="company-name"
            type="text"
            value={companyName}
            onChange={(e) => onCompanyNameChange(e.target.value)}
            placeholder={t("onboarding.step1.companyNamePlaceholder")}
          />
        </motion.div>

        {/* Workspace Name */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="workspace-name"
            className="text-[13px] font-medium text-secondary"
          >
            {t("onboarding.step1.label")}
          </label>
          <Input
            id="workspace-name"
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={t("onboarding.step1.placeholder")}
            autoFocus
          />
        </motion.div>

        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <span className="text-[13px] font-medium text-secondary">
            {t("onboarding.step1.accentColor")}
          </span>
          <div className="flex items-center gap-2">
            {ACCENT_COLOR_PRESET_KEYS.map(({ hex, key }) => {
              const isSelected = accentColor === hex;
              return (
                <button
                  key={hex}
                  type="button"
                  title={t(
                    `onboarding.step1.colors.${key}` as "onboarding.step1.colors.sage",
                  )}
                  onClick={() => onAccentColorChange(isSelected ? "" : hex)}
                  className="relative w-8 h-8 rounded-full border-2 transition-all cursor-pointer"
                  style={{
                    backgroundColor: hex,
                    borderColor: isSelected ? hex : "transparent",
                    boxShadow: isSelected
                      ? `0 0 0 2px var(--color-page), 0 0 0 4px ${hex}`
                      : undefined,
                  }}
                >
                  {isSelected && (
                    <CheckIcon className="absolute inset-0 m-auto w-4 h-4 text-white" />
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ── Step 2: Organization details with selection chips ──

function StepDetails({
  industry,
  companySize,
  jurisdiction,
  onIndustryChange,
  onCompanySizeChange,
  onJurisdictionChange,
}: {
  industry: string;
  companySize: ProfilingCompanySize | "";
  jurisdiction: string;
  onIndustryChange: (v: string) => void;
  onCompanySizeChange: (v: ProfilingCompanySize) => void;
  onJurisdictionChange: (v: string) => void;
}) {
  const { t } = useTranslation(["common", "audits"]);

  return (
    <div className="flex flex-col">
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("onboarding.step2.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("onboarding.step2.subtitle")}
        </p>
      </motion.div>

      <div className="mt-6 flex flex-col gap-5">
        {/* Industry */}
        <motion.div
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <p className="text-[13px] font-medium text-primary mb-2.5">
            {t("onboarding.step2.industry.label")}
          </p>
          <div className="flex flex-wrap gap-2">
            {INDUSTRY_KEYS.map((key) => (
              <SelectionChip
                key={key}
                label={t(
                  `onboarding.step2.industry.options.${key}` as "onboarding.step2.industry.options.technologySaas",
                )}
                selected={industry === key}
                onClick={() => onIndustryChange(key)}
              />
            ))}
          </div>
        </motion.div>

        {/* Company Size */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <p className="text-[13px] font-medium text-primary mb-2.5">
            {t("onboarding.step2.companySize.label")}
          </p>
          <div className="flex flex-wrap gap-2">
            {PROFILING_SIZES.map((opt) => (
              <SelectionChip
                key={opt}
                label={t(
                  `audits:wizard.profiling.companySize.options.${opt}` as "audits:wizard.profiling.companySize.options.1-20",
                )}
                selected={companySize === opt}
                onClick={() => onCompanySizeChange(opt)}
              />
            ))}
          </div>
        </motion.div>

        {/* Jurisdiction */}
        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <p className="text-[13px] font-medium text-primary mb-2.5">
            {t("onboarding.step2.jurisdiction.label")}
          </p>
          <div className="flex flex-wrap gap-2">
            {JURISDICTION_KEYS.map((key) => (
              <SelectionChip
                key={key}
                label={t(
                  `onboarding.step2.jurisdiction.options.${key}` as "onboarding.step2.jurisdiction.options.unitedStates",
                )}
                selected={jurisdiction === key}
                onClick={() => onJurisdictionChange(key)}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ── Step 3: Org Context (no skip button) ──

function StepOrgContextOnboarding({
  answers,
  onAnswersChange,
}: {
  answers: Partial<OrgContextAnswers>;
  onAnswersChange: (a: Partial<OrgContextAnswers>) => void;
}) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex flex-col">
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("onboarding.step3.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("onboarding.step3.subtitle")}
        </p>
      </motion.div>

      <div className="mt-5">
        <OrgContextForm answers={answers} onChange={onAnswersChange} compact />
      </div>
    </div>
  );
}

// ── Step 5: How do you want to start? ──

function StepStartMode({
  withDemoData,
  onWithDemoDataChange,
}: {
  withDemoData: boolean;
  onWithDemoDataChange: (v: boolean) => void;
}) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex flex-col">
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("onboarding.step4.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("onboarding.step4.subtitle")}
        </p>
      </motion.div>

      <div className="mt-6 flex flex-col gap-3">
        {/* Start fresh option */}
        <motion.button
          type="button"
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={() => onWithDemoDataChange(false)}
          className={`
            w-full text-left rounded-xl border p-4 transition-all duration-200 cursor-pointer
            ${
              !withDemoData
                ? "border-accent bg-accent/8 shadow-sm"
                : "border-border bg-white hover:bg-card-hover hover:border-border"
            }
          `}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${!withDemoData ? "bg-accent/15 text-accent" : "bg-muted text-secondary"}`}
            >
              <CheckCircleIcon className="size-4" />
            </div>
            <div>
              <p
                className={`text-[14px] font-semibold ${!withDemoData ? "text-accent" : "text-primary"}`}
              >
                {t("onboarding.step4.startFresh.title")}
              </p>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                {t("onboarding.step4.startFresh.description")}
              </p>
            </div>
          </div>
        </motion.button>

        {/* Demo data option */}
        <motion.button
          type="button"
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={() => onWithDemoDataChange(true)}
          className={`
            w-full text-left rounded-xl border p-4 transition-all duration-200 cursor-pointer
            ${
              withDemoData
                ? "border-accent bg-accent/8 shadow-sm"
                : "border-border bg-white hover:bg-card-hover hover:border-border"
            }
          `}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${withDemoData ? "bg-accent/15 text-accent" : "bg-muted text-secondary"}`}
            >
              <SparkleIcon className="size-4" />
            </div>
            <div>
              <p
                className={`text-[14px] font-semibold ${withDemoData ? "text-accent" : "text-primary"}`}
              >
                {t("onboarding.step4.exploreDemo.title")}
              </p>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                {t("onboarding.step4.exploreDemo.description")}
              </p>
            </div>
          </div>
        </motion.button>
      </div>
    </div>
  );
}
